// ---------------------------------------------------------------------------
// useSam3 — hook para segmentar imágenes aéreas con SAM 3 vía backend proxy
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';
import { segmentWithSam3Endpoint } from '../services/sam3Service';
import {
  Sam3ApiPrediction,
  Sam3ApiPromptResult,
  Sam3Config,
  Sam3DetectedPolygon,
  Sam3TextPrompt,
} from '../models/sam3Model';
import useAuth from '../../../hooks/useAuth';

const DEFAULT_CONFIG: Sam3Config = {
  prompts: ['house', 'building', 'rooftop'],
  confidenceThreshold: 0.4,
};

const SEGMENT_API_URL = process.env.REACT_APP_SEGMENT_API_URL ?? 'http://localhost:3001';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const imageUrlToBase64 = async (url: string): Promise<string> => {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = () => reject(new Error('FileReader falló'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('No se pudo crear contexto 2D'));
        ctx.drawImage(img, 0, 0);
        const base64 = canvas.toDataURL('image/jpeg').split(',')[1];
        if (!base64) return reject(new Error('Canvas tainted: la imagen no permite CORS'));
        resolve(base64);
      };
      img.onerror = () => reject(new Error(`No se pudo cargar la imagen: ${url}`));
      img.src = url;
    });
  }
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsDataURL(file);
  });

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const useSam3 = (config: Partial<Sam3Config> = {}) => {
  const mergedConfig: Sam3Config = {
    prompts: config.prompts ?? DEFAULT_CONFIG.prompts,
    confidenceThreshold: config.confidenceThreshold ?? DEFAULT_CONFIG.confidenceThreshold,
  };

  const { getAuthToken } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [polygons, setPolygons] = useState<Sam3DetectedPolygon[]>([]);
  const [promptsConfig, setPromptsConfig] = useState<string[]>(mergedConfig.prompts);
  const [threshold, setThreshold] = useState<number>(mergedConfig.confidenceThreshold);

  // ---------------------------------------------------------------------------
  // Segmentar desde URL
  // ---------------------------------------------------------------------------
  const segmentFromUrl = useCallback(
    async (imageUrl: string) => {
      setIsLoading(true);
      setError(null);
      setPolygons([]);
      try {
        const imageBase64 = await imageUrlToBase64(imageUrl);
        await _callSam3(imageBase64);
      } catch (e: any) {
        setError(e?.message ?? 'Error al procesar la imagen');
      } finally {
        setIsLoading(false);
      }
    },
    [promptsConfig, threshold],
  );

  // ---------------------------------------------------------------------------
  // Segmentar desde File (input[type=file])
  // ---------------------------------------------------------------------------
  const segmentFromFile = useCallback(
    async (file: File) => {
      setIsLoading(true);
      setError(null);
      setPolygons([]);
      try {
        const imageBase64 = await fileToBase64(file);
        await _callSam3(imageBase64);
      } catch (e: any) {
        setError(e?.message ?? 'Error al procesar el archivo');
      } finally {
        setIsLoading(false);
      }
    },
    [promptsConfig, threshold],
  );

  // ---------------------------------------------------------------------------
  // Segmentar desde base64 ya preparado
  // ---------------------------------------------------------------------------
  const segmentFromBase64 = useCallback(
    async (imageBase64: string) => {
      setIsLoading(true);
      setError(null);
      setPolygons([]);
      try {
        await _callSam3(imageBase64);
      } catch (e: any) {
        setError(e?.message ?? 'Error al segmentar');
      } finally {
        setIsLoading(false);
      }
    },
    [promptsConfig, threshold],
  );

  // ---------------------------------------------------------------------------
  // Llamada al backend proxy
  // ---------------------------------------------------------------------------
  const _callSam3 = async (imageBase64: string) => {
    const sam3Prompts: Sam3TextPrompt[] = promptsConfig
      .filter((p) => p.trim())
      .map((p) => ({ type: 'text', text: p.trim() }));

    if (!sam3Prompts.length) {
      throw new Error('Agrega al menos un prompt de texto');
    }

    const { call, controller } = segmentWithSam3Endpoint(
      imageBase64,
      sam3Prompts,
      threshold,
      getAuthToken(),
    );

    try {
      const response = await call(SEGMENT_API_URL);
      const data = response.data;

      console.debug('[SAM3] API response:', JSON.stringify(data, null, 2));

      // Response shape:
      // prompt_results[i].echo.text          → prompt label
      // prompt_results[i].predictions[j].confidence
      // prompt_results[i].predictions[j].masks → number[][][] (array of polygons, each = [[x,y],...])
      const promptResults: Sam3ApiPromptResult[] = data?.prompt_results ?? [];

      const detected: Sam3DetectedPolygon[] = promptResults.flatMap((result) => {
        const promptLabel = result?.echo?.text ?? 'detection';
        return (result?.predictions ?? []).flatMap((prediction: Sam3ApiPrediction) =>
          (prediction?.masks ?? []).map((polygon: number[][]) => ({
            points: polygon,
            confidence: prediction?.confidence ?? 0,
            promptLabel,
          })),
        );
      });

      setPolygons(detected);

      if (!detected.length) {
        console.warn('[SAM3] No se detectaron polígonos. Respuesta completa:', data);
      }
    } catch (e: any) {
      controller.abort();
      if (e?.response?.status === 401) throw new Error('Sesión expirada, vuelve a iniciar sesión');
      if (e?.response?.status === 429) throw new Error('Límite de requests alcanzado');
      if (e?.response?.status === 400) throw new Error(`Request inválido: ${e?.response?.data?.message ?? 'verifica el formato de la imagen'}`);
      throw new Error(e?.response?.data?.message ?? e?.message ?? 'Error al conectar con el servidor');
    }
  };

  const clearResults = useCallback(() => {
    setPolygons([]);
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    polygons,
    promptsConfig,
    setPromptsConfig,
    threshold,
    setThreshold,
    segmentFromUrl,
    segmentFromFile,
    segmentFromBase64,
    clearResults,
  };
};

export default useSam3;
